import {
  IPriceListPanelProps,
  IListPrices,
  ICommonListProps,
} from '../../areas/product/components/price-list/price-list-panel/types';
import { OptionTypeEnum } from 'src/components/filter-dropdown';
import { FilterTypeEnum, IFilter } from 'src/components/price-list/types';
import { Price, AlternativePrice, Maybe } from 'src/types';

const placeAfter = 1;
const placeBefore = -1;

export const getCommonListProps = (
  props: IPriceListPanelProps
): ICommonListProps => {
  return {
    productId: props.productId,
    productName: props.productName,
    media: props.media,
    brand: props.brand,
    campaign: props.campaign,
  };
};

const includeShipping = (filters: IFilter[]) =>
  filters.find(filter => filter.type === FilterTypeEnum.PRICES_INC_SHIPPING);

const getTotalPrice = (offer: Price, filters: IFilter[]) => {
  let total = offer.price?.exclShipping || 0;
  if (includeShipping(filters)) {
    const shippingCost = offer.shipping?.fastest?.shippingCost || 0;
    total += shippingCost;
  }

  return total;
};

const sortDownloadable = (a: Price, b: Price, filters: IFilter[]) => {
  const isDownloadable = a.type === 'download' || b.type === 'download';

  if (isDownloadable && b.store?.featured && a.store?.featured) {
    if (b.type !== 'download') {
      return placeBefore;
    }

    if (a.type === 'download') {
      const aTotal = getTotalPrice(a, filters || []);
      const bTotal = getTotalPrice(b, filters || []);
      if (aTotal <= bTotal) {
        return placeBefore;
      }

      return placeAfter;
    }

    return placeAfter;
  }

  return null;
};

const sortFeatured = (a: Price, b: Price) => {
  if (!a.store?.featured || !b.store?.featured) {
    if (a.store?.featured) {
      return placeBefore;
    }
    return placeAfter;
  }
  return null;
};

const sortStockStatus = (a: Price, b: Price, filters: IFilter[]) => {
  const statusesToCheck = ['unknown', 'not_in_stock', 'incoming'];
  const aStockStatusFail = statusesToCheck.includes(a.stock?.status || '');
  const bStockStatusFail = statusesToCheck.includes(b.stock?.status || '');

  if (aStockStatusFail || bStockStatusFail) {
    if (aStockStatusFail && b.stock?.status === 'unknown') {
      const aTotal = getTotalPrice(a, filters || []);
      const bTotal = getTotalPrice(b, filters || []);

      if (a.stock?.status === 'unknown' && aTotal > bTotal) {
        return placeAfter;
      }

      return placeBefore;
    }

    const isIncoming = a.stock?.status === 'incoming';
    if ((isIncoming && bStockStatusFail) || !aStockStatusFail) {
      return placeBefore;
    }

    return placeAfter;
  }

  return null;
};

const sortShipping = (a: Price, b: Price, filters: IFilter[]) => {
  const aShipping = a.shipping?.fastest?.deliveryDays;
  const bShipping = b.shipping?.fastest?.deliveryDays;

  if (!aShipping && bShipping) {
    return placeAfter;
  }

  if (aShipping) {
    if (!bShipping) {
      return placeBefore;
    }

    const aShippingTotal = aShipping.min + aShipping.max;
    const bShippingTotal = bShipping.min + bShipping.max;

    if (aShipping.min < bShipping.min) {
      return placeBefore;
    }

    if (aShipping.min === bShipping.min && aShippingTotal < bShippingTotal) {
      return placeBefore;
    }

    return placeAfter;
  }

  if (!bShipping) {
    const aTotal = getTotalPrice(a, filters || []);
    const bTotal = getTotalPrice(b, filters || []);

    if (aTotal < bTotal) {
      return placeBefore;
    }

    if (aTotal === bTotal) {
      return 0;
    }

    return placeAfter;
  }
  return null;
};

const sortAlternatives = (
  alternatives: Maybe<AlternativePrice>[],
  filters: IFilter[]
): Price[] | Maybe<AlternativePrice>[] => {
  return sortByFastest(alternatives, filters, true);
};

const sortByFastest = (
  offerList: Price[] | Maybe<AlternativePrice>[],
  filters: IFilter[],
  isAlternative: Boolean = false
): Price[] | Maybe<AlternativePrice>[] => {
  if (offerList.length <= 0) {
    return offerList;
  }

  const SortedOffersAndAlternatives = offerList.map(offer => {
    const newOffer: any = { ...offer };
    if (newOffer && newOffer.alternativePrices) {
      const alternatives = newOffer.alternativePrices;
      newOffer.alternativePrices = sortAlternatives(alternatives, filters);
    }
    return newOffer;
  });

  SortedOffersAndAlternatives.sort((a, b) => {
    const downloadableSort = sortDownloadable(a, b, filters);
    if (downloadableSort) {
      return downloadableSort;
    }

    const featuredSort = sortFeatured(a, b);
    if (featuredSort && !isAlternative) {
      return featuredSort;
    }

    const stockStatusSort = sortStockStatus(a, b, filters);
    if (stockStatusSort) {
      return stockStatusSort;
    }

    const shippingSort = sortShipping(a, b, filters);
    if (shippingSort) {
      return shippingSort;
    }

    return 0;
  });

  return SortedOffersAndAlternatives;
};

export const sortByFastestDelivery = (
  offerList: IListPrices,
  currentSorting: OptionTypeEnum,
  filters: IFilter[] = []
) => {
  const newOffers = { ...offerList };
  if (currentSorting === OptionTypeEnum.FASTEST_DELIVERY) {
    newOffers.mainList = sortByFastest(
      [...newOffers.mainList],
      filters
    ) as Price[];
  }
  return newOffers;
};
